import React, { useEffect, useMemo, useState } from 'react';
import { graphql, navigate } from 'gatsby';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Column, Grid, Row, Spacing, Tabs, Text } from '@dls/web';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { noop } from '@lux/helpers';
import {
  authActions,
  ngnisActions,
  orderActions,
  planActions,
  rootActions,
  userActions
} from '@detox/actions';
import { simCardType } from '../config/simcard-types';
import LoadingOverlayComponent from '../components/LoadingOverlay';
import NumberSelectNew from '../components/NumberSelectNew';
import NumberPortIn from '../components/NumberPortIn';
import SEO from '../components/SEO';
import isFeatureFlagEnabled from '../helpers/feature-flags';
import NumberSelectionAuth from '../components/NumberSelectionPageAuth';
import CHECKOUT_CONSTANTS from '../constants/checkout';

import { TOMO_INDICATORS } from '../constants/third-party';
import { useAddonsHelper } from '../hooks/useAddonsHelper';
import useThirdPartyIndicator from '../hooks/useThirdPartyIndicator';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ACTION_TYPES } from '../constants/actions';
import {
  dataLayerPush,
  flattenNodes,
  getAemConfigs,
  getErrorMessage,
  getExcludePreTickAddons,
  getSkipAddonsFromConfig,
  isPromotionPlan
} from '../helpers/common';
import {
  CHOOSE_NUMBER_TAB,
  CHOOSE_SIM_TYPE
} from '../constants/choose-sim-type';
import { FlowType } from '../types/dataLayer.type';
import {
  getChangeFlowDataLayer,
  getChooseAddonsDataLayer
} from '../helpers/dataLayerHelpers';
import { ORDER } from '../constants';
import { getUIAMEnabledWithToken } from '../selectors';
import { FEATURE_FLAG_SPECIAL_PROMO } from '../types/featureFlag.types';
import { setSpecialPromo } from '../reducers/promotions';
import { AEM_CONFIG_KEYS } from '../config/common';
import { useSpecialPromo } from '../hooks/useSpecialPromo';
import { useABTesting } from '@wec-core/ab-testing';
import { AbFeatureNames } from '../constants/ab-feature-names';
import ORDER_CONSTANTS from '../constants/order';
import { getEshopCreateOrderParams } from '../helpers/order';
import { NUMBER_SELECTION_TAB_ITEMS } from '../config/modals';
import { setErrorData } from '../reducers/error';

const isGomoFlow = isFeatureFlagEnabled('GOMO_FLOW');
const is5GSAEnabled = isFeatureFlagEnabled('FEATURE_FLAG_ENABLE_5GSA');

const tabItems = NUMBER_SELECTION_TAB_ITEMS;

export const NumberSelectionPage = ({
  createMobileShareNewNumberOrder,
  createNewNumberOrder,
  assignNumberToOrder,
  createPortinOrder,
  setProductOrderError,
  resetSearchNumbers,
  location,
  ngnis,
  ngnisSearchNumbersError,
  order,
  searchNumbers,
  searchNumberFromNumberPool,
  selectedProduct,
  selectedPlan,
  selectedService,
  setPortInNumber,
  setAppFlag,
  user,
  data,
  worryFreeStatus,
  resetByKeys,
  getAuthToken,
  getUserInformation,
  auth,
  resetWorryFree,
  setCheckoutNewNumberFlow = () => {},
  accessories,
  setSelectedPlan,
  apigeeTokenEnabled,
  setSpecialPromo,
  addonsLoading,
  specialPromoCode,
  product
}) => {
  const {
    hasIndicator,
    indicator: thirdPartyIndicator
  } = useThirdPartyIndicator(TOMO_INDICATORS);

  let {
    allAddonPreTick,
    allChooseSimOptions,
    allPlan,
    allVariableConfig,
    allAddon,
    allPhone: allApplePhone
  } = data || {};
  const { addonSimOnlyPlan } = data;
  allAddon = useMemo(() => flattenNodes(allAddon), [allAddon]);
  allApplePhone = useMemo(() => flattenNodes(allApplePhone), [allApplePhone]);
  allAddonPreTick = useMemo(() => (data ? flattenNodes(allAddonPreTick) : []), [
    allAddonPreTick,
    data
  ]);
  const appleCareAddons = useMemo(
    () =>
      allAddon.filter(({ productTitle }) => productTitle.match(/AppleCare/i)),
    [allAddon]
  );
  const plans = useMemo(() => flattenNodes(allPlan), [allPlan]);
  const isMobileShareNew = selectedPlan?.mobileShareOrderType === 'MS_NEW';
  const aemVariableConfigs = flattenNodes(allVariableConfig) || [];
  const [
    skipAddonsConfig = '',
    specialPromoPortInConfig = '',
    numberPoolLockDurationInMinsRaw,
    numberPoolLinesCount = '12'
  ] = getAemConfigs(aemVariableConfigs, [
    AEM_CONFIG_KEYS.DETOX_SKIP_ADDONS_FOR_PLANS,
    AEM_CONFIG_KEYS.DETOX_SPECIAL_PROMO_HIDE_PORTIN,
    AEM_CONFIG_KEYS.NUMBER_POOL_LOCK_DURATION,
    AEM_CONFIG_KEYS.NUMBER_POOL_LINES_COUNT
  ]);
  const numberPoolLockDurationInMins =
    !isNaN(Number(numberPoolLockDurationInMinsRaw)) &&
    Number(numberPoolLockDurationInMinsRaw) > 0
      ? Number(numberPoolLockDurationInMinsRaw)
      : 120;

  const {
    isFeatureAllowed: [
      abTestNumberPool,
      abTestCreateOrder,
      enhancedErrorEnabled
    ],
    isSuccess
  } = useABTesting({
    featureName: [
      AbFeatureNames.NUMBER_POOL,
      AbFeatureNames.ESHOP_CREATE_ORDER,
      AbFeatureNames.ENHANCED_ERROR_HANDLER
    ]
  });

  const [selectedTab, setSelectedTab] = useState(
    () =>
      (typeof window !== 'undefined' && window.location.hash) ||
      location?.hash ||
      CHOOSE_NUMBER_TAB.NEW
  );

  const isSpecialPromoEnabled = isFeatureFlagEnabled(
    FEATURE_FLAG_SPECIAL_PROMO
  );
  const [errorCount, setErrorCount] = useState(0);
  const [errorStatus, setErrorStatus] = useState('');
  const orderError = order?.error;
  const { selectedAccessories, promotions, thirdParty } = useSelector(
    state => ({
      selectedAccessories: state.accessory?.selectedAccessories,
      promotions: state.promotions,
      thirdParty: state.thirdParty
    })
  );

  const { specialPromo } = promotions;
  const skipAddons = getSkipAddonsFromConfig(
    skipAddonsConfig,
    selectedPlan?.planRelId,
    specialPromo
  );
  let pageTitle;
  const editNewNumberMode = location?.state?.editNewNumberMode;

  const dispatch = useDispatch();

  const tabItemsToShow = useMemo(() => {
    const hiddenPortInPromoCodes = specialPromoPortInConfig
      ? specialPromoPortInConfig
          .split(',')
          .map(code => code.trim())
          .filter(code => code)
      : [];

    // Check if current specialPromoCode is in the list of codes that should hide port-in
    const shouldHidePortIn =
      specialPromoCode && hiddenPortInPromoCodes.includes(specialPromoCode);
    if (shouldHidePortIn) {
      return tabItems.filter(item => item.value !== CHOOSE_NUMBER_TAB.PORT_IN);
    }

    const isPromotionOffer =
      promotions?.activePromotion && promotions?.promotionLoaded;
    const isPromoPlan = isPromotionPlan(promotions, selectedPlan);
    if (isPromoPlan && isPromotionOffer) {
      const tabOptions = promotions?.transactionOptions || [];
      if (tabOptions.length > 0) {
        return tabItems.filter(item => tabOptions.includes(item.ref));
      }
    }

    return tabItems;
  }, [
    specialPromoCode,
    specialPromoPortInConfig,
    promotions?.activePromotion,
    promotions?.promotionLoaded,
    promotions?.transactionOptions
  ]);

  useAddonsHelper({
    allAddon,
    addonSimOnlyPlan,
    allApplePhone,
    appleCareAddons,
    skipAddonsConfig,
    loadOnInitiate: true
  });

  let { allErrorMessage } = data || {};
  const isNewNumberTab = selectedTab === CHOOSE_NUMBER_TAB.NEW;
  const showNewNumberLoading = isNewNumberTab && ngnis.searchNumbers.loading;
  const [isRecaptchaLoading, setRecaptchaLoading] = useState(false);
  const showLoadingOverlay =
    order.loading || addonsLoading || isRecaptchaLoading;
  allErrorMessage = useMemo(
    () => (data ? flattenNodes(data.allErrorMessage) : []),
    [allErrorMessage, data]
  );

  const genericErrorMessage = getErrorMessage(
    thirdParty.errorCode,
    allErrorMessage
  );

  const getSimTypeValue = selectedSIMTypeValue => {
    const simType5GValue =
      selectedSIMTypeValue === CHOOSE_SIM_TYPE.SIM_TYPE_ESIM
        ? simCardType.simTypeESim
        : simCardType.simType5G;
    return is5GSAEnabled ? simType5GValue : simCardType.simType4G;
  };

  const resetErrorMsg = () => {
    dispatch({
      type: ACTION_TYPES.THIRD_PARTY.UPDATE_INDICATOR_SUCCESS
    });
  };

  /** Handle error when thirdparty indicator is true */
  const { ctaText, ctaURL, title, description } = genericErrorMessage || {};
  const thirdPartyErrorState = useMemo(
    () => ({
      ...thirdParty,
      hasError: hasIndicator && thirdParty.hasError,
      errorMessage: description,
      errorTitle: title,
      btnText: ctaText,
      redirectNeeded: true,
      redirectUrl: ctaURL
    }),
    [thirdParty.hasError]
  );
  const { renderError } = useErrorHandler({
    states: [thirdPartyErrorState, order, ngnis?.searchNumbers, user],
    onCloseErrorPopUp: resetErrorMsg,
    configs: {
      reloadOnClose: true,
      enhancedErrorHandler: enhancedErrorEnabled,
      suppressError: !enhancedErrorEnabled
    }
  });

  if (isMobileShareNew) {
    pageTitle = 'Choose a new MobileShare number';
  } else if (editNewNumberMode) {
    pageTitle = 'Select a new number';
  } else {
    pageTitle = 'Get started with your mobile line';
  }

  const isUserLoggedin = Boolean(user?.information);

  useEffect(() => {
    dispatch(setErrorData(flattenNodes(data.allErrorMessage)));
  }, []);

  // Get Auth Token when component first renders
  useEffect(() => {
    if (apigeeTokenEnabled) {
      const cleanWorryFree = auth.worryFree.otpVerified;

      if (!auth.sessionToken || cleanWorryFree) {
        // We get token only when there is no session token,
        // or when user has logged in via WF before and needs cleaning
        getAuthToken({ cleanWorryFree });
      } else if (auth.sessionToken && auth.worryFree.status) {
        // Reset worry free error statuses if any
        resetWorryFree();
      }
    }
  }, [
    auth.worryFree.otpVerified,
    auth.worryFree.status,
    getAuthToken,
    resetWorryFree,
    auth.sessionToken,
    apigeeTokenEnabled
  ]);

  useEffect(() => {
    //Check for user information
    const getUser = async () => {
      if (!isUserLoggedin) {
        await getUserInformation();
      }
    };
    if (apigeeTokenEnabled) {
      getUser();
    }
  }, [apigeeTokenEnabled]);

  useSpecialPromo({ aemVariableConfigs, plans });

  useEffect(() => {
    if (!apigeeTokenEnabled || !isSuccess || !isUserLoggedin) return undefined;
    if (selectedTab !== CHOOSE_NUMBER_TAB.NEW) return undefined;

    let active = true;

    const fetchNumbers = async () => {
      if (worryFreeStatus) {
        resetByKeys(['user', 'auth']);
        await getAuthToken(true);
      }
      if (!active) return;

      if (abTestNumberPool) {
        searchNumberFromNumberPool(numberPoolLinesCount);
      } else {
        searchNumbers();
      }
    };

    fetchNumbers();

    return () => {
      active = false; // guard late dispatch; DO NOT reset search here
    };
  }, [
    apigeeTokenEnabled,
    isSuccess,
    isUserLoggedin,
    selectedTab,
    worryFreeStatus,
    abTestNumberPool,
    numberPoolLinesCount,
    resetByKeys,
    getAuthToken,
    searchNumberFromNumberPool,
    searchNumbers
  ]);

  // Reset the numbers returned from NGNIS ONLY when the page unmounts.
  useEffect(() => {
    return () => {
      resetSearchNumbers();
    };
  }, [resetSearchNumbers]);

  const _getEshopCreateOrderParams = ({
    newNumber,
    simType,
    orderType = ORDER_CONSTANTS.TYPE.NEW,
    captchaToken
  }) => {
    if (!abTestCreateOrder) return;
    return getEshopCreateOrderParams({
      selectedPlan,
      selectedProduct,
      newNumber,
      selectedAccessories: accessories,
      simType: simType || product.selectedSimType,
      orderType,
      captchaToken,
      cisToken: user.cis?.information?.sessionToken
    });
  };

  const getRecaptchaResponse = async () => {
    setRecaptchaLoading(true);
    let reCaptchaResponse = '';
    if (typeof executeRecaptcha === 'function') {
      reCaptchaResponse = await executeRecaptcha();
    }
    setRecaptchaLoading(false);
    return reCaptchaResponse;
  };

  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleNewNumberClick = async (number, selectedSIMType) => {
    setCheckoutNewNumberFlow();
    pushDataLayer(selectedSIMType, ORDER.TYPE.NEW);
    const captchaResponse = abTestNumberPool && (await getRecaptchaResponse());
    if (selectedPlan && isMobileShareNew) {
      const selectedMainLine = user.mainLines[selectedService.serviceId];
      createMobileShareNewNumberOrder({
        ...(specialPromo?.promoCode && {
          hasSpecialPromo: specialPromo?.promoCode
        }),
        service: selectedService,
        mainLineGroupId: selectedMainLine?.groupId,
        planId: selectedPlan.planId,
        planRelId: selectedPlan.planRelId,
        numbers: ngnis.searchNumbers.numbers,
        resourceNumber: number,
        signature: ngnis.searchNumbers.signature,
        preTickAddons: allAddonPreTick,
        selectedProduct: selectedProduct?.groupId,
        simCardType: getSimTypeValue(selectedSIMType),
        contact: user?.information?.clientContext?.contact,
        isFeatureAllowed: abTestNumberPool,
        lockDuration: numberPoolLockDurationInMins,
        ...(abTestNumberPool && {
          captchaResponse
        }),
        createEshopOrderParams: _getEshopCreateOrderParams({
          simType: selectedSIMType,
          newNumber: number,
          orderType: ORDER_CONSTANTS.TYPE.MS_NEW,
          captchaToken: captchaResponse
        })
      });
    } else {
      createNewNumberOrder({
        ...(hasIndicator && { thirdPartyIndicator }),
        ...(specialPromo?.promoCode &&
          isSpecialPromoEnabled && {
            hasSpecialPromo: specialPromo?.promoCode
          }),
        planId: selectedPlan.planId,
        planRelId: selectedPlan.planRelId,
        simpleProductSpecId: selectedProduct?.productId,
        numbers: ngnis.searchNumbers.numbers,
        resourceNumber: number,
        signature: ngnis.searchNumbers.signature,
        cisDetails: user.cis.information,
        preTickAddons: getExcludePreTickAddons(
          isSpecialPromoEnabled,
          allAddonPreTick,
          specialPromo,
          selectedPlan
        ),
        monthlyTerm: selectedProduct?.monthlyTerm,
        selectedProduct: selectedProduct?.groupId,
        simCardType: getSimTypeValue(selectedSIMType),
        contact: user?.information?.clientContext?.contact,
        planTags: selectedPlan.tags,
        accessories: selectedAccessories,
        isFeatureAllowed: abTestNumberPool,
        lockDuration: numberPoolLockDurationInMins,
        ...(abTestNumberPool && {
          captchaResponse
        }),
        createEshopOrderParams: _getEshopCreateOrderParams({
          simType: selectedSIMType,
          newNumber: number,
          captchaToken: captchaResponse
        })
      });
    }
  };

  const handlePortInClick = (number, selectedTelcoService, selectedSIMType) => {
    pushDataLayer(selectedSIMType, ORDER.TYPE.PORTIN);
    createPortinOrder({
      ...(specialPromo?.promoCode &&
        isSpecialPromoEnabled && {
          hasSpecialPromo: specialPromo?.promoCode
        }),
      planId: selectedPlan.planId,
      planRelId: selectedPlan.planRelId,
      simpleProductSpecId: selectedProduct?.productId,
      portinNumber: number,
      cisDetails: user.cis.information,
      preTickAddons: getExcludePreTickAddons(
        isSpecialPromoEnabled,
        allAddonPreTick,
        specialPromo,
        selectedPlan
      ),
      monthlyTerm: selectedProduct?.monthlyTerm,
      selectedProduct: selectedProduct?.groupId,
      simCardType: getSimTypeValue(selectedSIMType),
      contact: user?.information?.clientContext?.contact,
      planTags: selectedPlan.tags,
      accessories: selectedAccessories,
      createEshopOrderParams: _getEshopCreateOrderParams({
        simType: selectedSIMType,
        newNumber: number,
        orderType: ORDER_CONSTANTS.TYPE.PORTIN
      }),
      telcoOperator: selectedTelcoService
    });

    setPortInNumber(number);
  };

  useEffect(() => {
    if (orderError) {
      setErrorStatus(orderError.status);
    }

    if (orderError?.type === 'newNumber' && errorCount < 3) {
      setErrorCount(count => count + 1);
    } else if (
      orderError &&
      orderError.type === 'newNumber' &&
      errorCount >= 3
    ) {
      setErrorStatus('NGNIS_NUMBER_SELECTED_EXCEED_LIMIT_ERROR');
    }
  }, [errorCount, orderError]);

  useEffect(() => {
    setAppFlag({ skipAddons });
  }, [skipAddons]);

  const ngnisErrorStatus = orderError?.type === 'newNumber' && errorStatus;

  // If GOMO flow is disabled and the port in flow is a singtel telco service
  // then we show it as an error prompt to the user
  const blockSingtelPortIn =
    !isGomoFlow &&
    (order.productOrder?.telcoType === 'SINGTELPOSTPAID' ||
      order.productOrder?.telcoType === 'SINGTELPREPAID');

  // we also show the generic error message if port in failed
  const portInErrorStatus = blockSingtelPortIn
    ? 'PORT_IN_NUMBER_NOT_ELIGIBLE'
    : orderError?.type === 'portIn' && errorStatus;

  const handleOnRefresh = () => {
    // Reset the error count to 0 on refresh
    setErrorCount(0);
    // Search for a new set of numbers from NGNIS on refresh
    abTestNumberPool ? searchNumberFromNumberPool('12') : searchNumbers();
  };

  const pushEcommerceDataLayer = flow => {
    dataLayerPush(
      getChangeFlowDataLayer({
        selectedPlan,
        selectedVariant: selectedProduct,
        accessories,
        flow,
        location: window.location
      }),
      true
    );
  };

  /**
   * Switch tab.
   *
   * FIX: We DO NOT call gatsby `navigate` here anymore. Going through navigate
   * fires a route change, and the app cancels in-flight requests on route
   * change — which aborted the NGNIS search. Instead we set local tab state and
   * update the URL hash via history.replaceState (keeps deep-linking / refresh
   * behaviour, matching the old `replace: true`) WITHOUT a route change, so the
   * search dispatched by the fetch effect is never cancelled.
   *
   * @param {string} tab
   */
  const handleTabChange = tab => {
    const isPromoPlan = isPromotionPlan(promotions, selectedPlan);
    const tabOptions = promotions?.transactionOptions || [];
    if (isPromoPlan && tabOptions.length === 1) {
      tab = NUMBER_SELECTION_TAB_ITEMS.find(
        ({ ref }) => ref === tabOptions[0]
      ).value;
    }

    if (tab === CHOOSE_NUMBER_TAB.NEW) {
      pushEcommerceDataLayer(FlowType.NEW);
    } else {
      pushEcommerceDataLayer(FlowType.PORT_IN);
    }
    setProductOrderError(false);

    setSelectedTab(tab);

    if (typeof window !== 'undefined') {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${tab}`
      );
    }
  };

  const pushDataLayer = (selectedSimType, flowType) => {
    dataLayerPush(
      getChooseAddonsDataLayer({
        selectedPlan,
        selectedVariant: selectedProduct,
        accessories: selectedAccessories,
        location: window.location,
        order: { sim: { selectedSimType }, type: flowType }
      }),
      true
    );
  };

  const handleNumberClick = async (
    number,
    selectedTelcoService = '',
    selectedSIMType
  ) => {
    if (editNewNumberMode) {
      await assignNumberToOrder({
        numbers: ngnis.searchNumbers.numbers,
        resourceNumber: number,
        signature: ngnis.searchNumbers.signature,
        order: order.productOrder,
        relaxCompatibilityRules: false,
        returnFullResponse: true,
        isFeatureAllowed: abTestNumberPool,
        lockDuration: numberPoolLockDurationInMins,
        ...(abTestNumberPool && {
          captchaResponse: await getRecaptchaResponse()
        })
      });

      return navigate(location.state.editNewNumberRedirectUrl);
    } else if (selectedTab === CHOOSE_NUMBER_TAB.NEW) {
      handleNewNumberClick(number, selectedSIMType);
    } else if (selectedTab === CHOOSE_NUMBER_TAB.PORT_IN) {
      handlePortInClick(number, selectedTelcoService, selectedSIMType);
    }
  };

  // Render the selected components based on the selected tab
  const renderTabContent = () => {
    const renderNewNumber = (
      <NumberSelectNew
        isLoading={showNewNumberLoading}
        errorCount={errorCount}
        errorStatus={ngnisErrorStatus}
        isNgnisError={ngnisSearchNumbersError}
        numbers={ngnis.searchNumbers.numbers}
        onClick={handleNumberClick}
        onModalClose={() => setProductOrderError(false)}
        onRefresh={() => handleOnRefresh()}
        chooseSIMOptions={flattenNodes(allChooseSimOptions)}
        selectedProduct={selectedProduct}
        skipAddons={skipAddons}
      />
    );

    const renderPortIn = (
      <NumberPortIn
        errorStatus={
          selectedTab === CHOOSE_NUMBER_TAB.PORT_IN &&
          ((portInErrorStatus && 'PORT_IN_NUMBER_NOT_ELIGIBLE') ||
            (orderError.errorMsg && 'PORT_IN_NUMBER_GENERIC_ERROR'))
        }
        onClick={handleNumberClick}
        onModalClose={() => {
          setProductOrderError(false);
          dispatch({ type: ACTION_TYPES.ORDER.RESET_CHOOSE_NUMBER_ERROR });
        }}
        chooseSIMOptions={flattenNodes(allChooseSimOptions)}
        selectedProduct={selectedProduct}
        skipAddons={skipAddons}
      />
    );

    if (selectedTab === CHOOSE_NUMBER_TAB.PORT_IN && !isMobileShareNew) {
      return renderPortIn;
    }
    // Default to the new-number content (covers '#new-number' and any other hash)
    return renderNewNumber;
  };

  return (
    <>
      <SEO title="New/Port-in" />
      {showLoadingOverlay && (
        <LoadingOverlayComponent data-testid="loading-overlay" />
      )}
      <NumberSelectionAuth>
        <Grid>
          <Row>
            <Column xs={12} md={8}>
              <Row>
                <Column noGutter>
                  <Spacing bottom={2}>
                    <Text type="pageTitle">{pageTitle}</Text>
                  </Spacing>
                </Column>
              </Row>
              <Row>
                <Column noGutter>
                  {!isMobileShareNew && !editNewNumberMode && (
                    <Spacing bottom={2}>
                      <Tabs
                        variant="scrollable"
                        scrollButtons="auto"
                        tabs={tabItemsToShow}
                        selected={tabItems.findIndex(
                          item => item.value === selectedTab
                        )}
                        onTabItemClick={(_e, index) =>
                          handleTabChange(tabItems[index].value)
                        }
                      />
                    </Spacing>
                  )}
                  {renderTabContent()}
                </Column>
              </Row>
            </Column>
          </Row>
        </Grid>
        {renderError()}
      </NumberSelectionAuth>
    </>
  );
};

NumberSelectionPage.defaultProps = {
  resetOrder: noop,
  resetSearchNumbers: noop,
  searchNumbers: noop,
  setPortInNumber: noop
};

NumberSelectionPage.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
  createProductOrder: PropTypes.func,
  ngnisSearchNumbersError: PropTypes.bool,
  resetOrder: PropTypes.func,
  resetSearchNumbers: PropTypes.func,
  searchNumbers: PropTypes.func,
  selectedProduct: PropTypes.object,
  selectedPlan: PropTypes.object,
  selectedService: PropTypes.object,
  setPortInNumber: PropTypes.func,
  ngnis: PropTypes.object,
  order: PropTypes.object,
  user: PropTypes.object
};

/* istanbul ignore next */
const mapStateToProps = state => {
  return {
    ngnisSearchNumbersError: state.ngnis.searchNumbers.error,
    selectedProduct: state.product.selectedProduct,
    accessories: state.accessory?.selectedAccessories,
    selectedPlan: state.plan.selectedPlan,
    selectedService: state.user.selectedService,
    ngnis: state.ngnis,
    order: state.order,
    user: state.user,
    auth: state.auth,
    worryFreeStatus: state.auth.worryFree.status,
    apigeeTokenEnabled: getUIAMEnabledWithToken(state),
    addonsLoading: state.addons.isSkeletonLoading,
    ShoppingCartLoading: false,
    specialPromoCode: state?.promotions?.specialPromo?.promoCode,
    product: state.product
  };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => {
  const {
    searchNumbers,
    resetSearchNumbers,
    searchNumberFromNumberPoolApi
  } = ngnisActions;
  const {
    createMobileShareNewNumberOrder,
    createNewNumberOrder,
    createPortinOrder,
    setProductOrderError,
    assignNumberToOrder,
    setProductOrderLoading
  } = orderActions;
  const { setSelectedPlan } = planActions;
  const { getAuthToken, resetWorryFree } = authActions;
  const { getUserInformation } = userActions;
  const { resetByKeys } = rootActions;

  return {
    setSpecialPromo: specialPromo => {
      dispatch(setSpecialPromo(specialPromo));
    },
    setSelectedPlan: plan => dispatch(setSelectedPlan(plan)),
    getUserInformation: () => dispatch(getUserInformation()),
    createMobileShareNewNumberOrder: options =>
      dispatch(createMobileShareNewNumberOrder(options)),
    createNewNumberOrder: options => dispatch(createNewNumberOrder(options)),
    createPortinOrder: options => dispatch(createPortinOrder(options)),
    setProductOrderError: error => dispatch(setProductOrderError(error)),
    resetSearchNumbers: () => dispatch(resetSearchNumbers()),
    searchNumbers: options => dispatch(searchNumbers(options)),
    searchNumberFromNumberPool: numberOfLines =>
      dispatch(searchNumberFromNumberPoolApi(numberOfLines)),
    assignNumberToOrder: options => dispatch(assignNumberToOrder(options)),
    getAuthToken: options => dispatch(getAuthToken(options)),
    resetWorryFree: () => dispatch(resetWorryFree()),
    setProductOrderLoading: options =>
      dispatch(setProductOrderLoading(options)),
    resetByKeys: keys => dispatch(resetByKeys(keys)),
    setCheckoutNewNumberFlow: () => {
      dispatch({
        type: CHECKOUT_CONSTANTS.SET_CHECKOUT_FLOW,
        payload: CHECKOUT_CONSTANTS.CHECKOUT_NEW_NUMBER
      });
    },
    setPortInNumber: number =>
      dispatch({
        type: 'SET_PORT_IN_NUMBER',
        payload: {
          portInNumber: number
        }
      }),
    setAppFlag: value => {
      dispatch({
        type: ACTION_TYPES.SETTINGS.SET_APP_CONFIG,
        payload: value
      });
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NumberSelectionPage);

export const query = graphql`
  {
    allPlan {
      edges {
        node {
          ...PlanDetailFragment
        }
      }
    }
    allAddon {
      edges {
        node {
          flowType
          contract
          groupName
          id
          onetimePrice
          phoneList
          price
          productDescription
          productSpecContainmentID
          productTitle
          ranking
          salesRanking
          atomic
          type
          promotionText
          requiredDevice
          addonPopupTitle
          addonPopupMessage
          addonPopupButtonLabel
          categoryId
          categorySequence
        }
      }
    }
    addonSimOnlyPlan {
      data {
        addonId
        price
        quantity
        unit
      }
      sms {
        addonId
        price
        quantity
        unit
      }
      talktime {
        addonId
        price
        quantity
        unit
      }
    }
    allPhone(filter: { brand: { eq: "Apple" } }) {
      edges {
        node {
          id
          groupId
          productId
          variants {
            productId
          }
        }
      }
    }
    allAddonPreTick(filter: { productId: { in: [null, ""] } }) {
      edges {
        node {
          boIds
          id
          phoneList
          phoneSpecific
          planIds
          segment
          spsIds
          transactionBoIds {
            gaBoIds
            portinBoIds
            reconBoIds
          }
          transactionSpsIds {
            gaSpsIds
            portinSpsIds
            reconSpsIds
          }
          transactionSpecific
          type
        }
      }
    }
    allErrorMessage {
      edges {
        node {
          ...AemComponentsFragment
        }
      }
    }
    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
    allChooseSimOptions {
      edges {
        node {
          notAvailable
          footerNotAvailable
          simTitle
          simTypeId
          simDetails {
            transactionType
            descWithDevice
            descWithoutDevice
            footerWithDevice
            footerWithoutDevice
            simPrice
          }
        }
      }
    }
  }
`;
